import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';

// Organisation (tenant) administration and switching between organisations.

interface DataEnvelope<T> {
  data: T;
}

export interface TenantRow {
  id: number;
  name: string;
  slug: string | null;
  parent_id: number | null;
  parent_name: string | null;
  created_at: string | null;
  user_count: number;
  sso_configured: boolean;
  /** The signed-in person can switch into it. */
  has_access: boolean;
  is_home: boolean;
  /** Superusers only; null otherwise. */
  allow_child_tenants: boolean | null;
}

export interface CreateTenantInput {
  name: string;
  slug?: string;
  parent_id?: number | null;
  grant_me_access: boolean;
  first_admin?: { email: string; username?: string; password?: string } | null;
}

export interface TenantAccessRow {
  id: number;
  tenant_id: number;
  home_user: {
    id: number;
    username: string;
    email: string;
    tenant_id: number;
    tenant_name: string;
  };
  linked_user_id: number;
  linked_user_active: boolean;
  granted_by: { id: number; username: string } | null;
  created_at: string;
}

export const useTenants = (enabled = true) =>
  useQuery({
    queryKey: ['tenants'],
    enabled,
    queryFn: async () => {
      const res = await api.request<DataEnvelope<TenantRow[]>>('/api/tenants');
      return res.data;
    },
  });

export const useCreateTenant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateTenantInput) => {
      const res = await api.request<DataEnvelope<TenantRow>>('/api/tenants', {
        method: 'POST',
        body,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
};

export const useUpdateTenant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: number; name?: string; slug?: string }) => {
      const res = await api.request<DataEnvelope<TenantRow>>(`/api/tenants/${id}`, {
        method: 'PATCH',
        body,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
};

export const useTenantAccess = (tenantId: number | null) =>
  useQuery({
    queryKey: ['tenant-access', tenantId],
    enabled: tenantId !== null,
    queryFn: async () => {
      const res = await api.request<DataEnvelope<TenantAccessRow[]>>(
        `/api/tenants/${tenantId}/access`
      );
      return res.data;
    },
  });

export const useGrantTenantAccess = (tenantId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const res = await api.request<DataEnvelope<TenantAccessRow>>(
        `/api/tenants/${tenantId}/access`,
        { method: 'POST', body: { user_id: userId } }
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-access', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
};

export const useRevokeTenantAccess = (tenantId: number | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (accessId: number) =>
      api.request(`/api/tenants/${tenantId}/access/${accessId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-access', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
};

/**
 * Switch organisation, then reload the app. A full reload is deliberate:
 * cached queries, map state and contexts all hold the previous
 * organisation's data, and hot-swapping them would leak it on screen.
 */
export const switchTenant = async (tenantId: number, landing = '/projects') => {
  await api.request('/api/me/switch-tenant', { method: 'POST', body: { tenant_id: tenantId } });
  window.location.assign(landing);
};

export const switchHome = async (landing = '/projects') => {
  await api.request('/api/me/switch-home', { method: 'POST' });
  window.location.assign(landing);
};

// ── FiSci entitlement ────────────────────────────────────────────────────────

export interface ChildTenantsSetting {
  can_edit: boolean;
  allow_child_tenants: boolean;
}

export const useChildTenantsSetting = () =>
  useQuery({
    queryKey: ['child-tenants-setting'],
    queryFn: async () => {
      const res = await api.request<DataEnvelope<ChildTenantsSetting>>(
        '/api/admin/tenant/child-tenants'
      );
      return res.data;
    },
  });

export const useUpdateChildTenantsSetting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (allow: boolean) => {
      const res = await api.request<DataEnvelope<ChildTenantsSetting>>(
        '/api/admin/tenant/child-tenants',
        { method: 'PATCH', body: { allow_child_tenants: allow } }
      );
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['child-tenants-setting'], data);
    },
  });
};
